import * as SelectPrimitive from '@radix-ui/react-select';
import { LuChevronDown, LuChevronUp, LuDatabase } from 'react-icons/lu';
import type { ReactNode } from 'react';
import { GROUP_BY_OPTIONS } from '../../constants';
import type { GroupByValue } from '../../constants';

type Props = {
  value: GroupByValue | undefined;
  onChange: (value: GroupByValue | undefined) => void;
  /** 表示する選択肢を呼び出し側から絞り込む（未指定なら GROUP_BY_OPTIONS 全件） */
  options?: ReadonlyArray<{ readonly value: GroupByValue; readonly label: string }>;
};

const GROUP_NONE_VALUE = '__none__';

export default function GroupBySelect({ value, onChange, options = GROUP_BY_OPTIONS }: Props) {
  const hasSelection = value !== undefined;

  return (
    <SelectPrimitive.Root
      value={value ?? GROUP_NONE_VALUE}
      onValueChange={(v) => onChange(v === GROUP_NONE_VALUE ? undefined : (v as GroupByValue))}
    >
      <SelectPrimitive.Trigger
        className={`group flex h-[42px] w-40 items-center gap-1 rounded border border-solid bg-white px-2 text-sm data-[state=open]:border-2 data-[state=open]:border-primary data-[state=open]:text-primary ${
          hasSelection
            ? 'border-primary bg-primary_lighten text-primary'
            : 'border-border text-main'
        }`}
      >
        <div className="flex min-w-0 flex-1 items-center gap-1 whitespace-nowrap">
          <LuDatabase
            className={`size-5 shrink-0 group-data-[state=open]:text-primary ${
              hasSelection ? 'text-primary' : 'text-sub'
            }`}
          />
          <SelectPrimitive.Value placeholder="グループ" />
        </div>
        <SelectPrimitive.Icon asChild>
          <span className="flex size-5 shrink-0 items-center justify-center">
            <LuChevronDown className="size-5 shrink-0 group-data-[state=open]:hidden" />
            <LuChevronUp className="hidden size-5 shrink-0 group-data-[state=open]:block" />
          </span>
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>

      <SelectPrimitive.Portal>
        <SelectPrimitive.Content
          align="start"
          sideOffset={3}
          position="popper"
          className="z-[9999] w-40 overflow-hidden rounded bg-white shadow-[0px_0px_12px_0px_rgba(0,0,0,0.2)]"
        >
          <SelectPrimitive.Viewport>
            <GroupBySelectItem value={GROUP_NONE_VALUE}>グループ</GroupBySelectItem>
            {options.map((opt) => (
              <GroupBySelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </GroupBySelectItem>
            ))}
          </SelectPrimitive.Viewport>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  );
}

function GroupBySelectItem({ value, children }: { value: string; children: ReactNode }) {
  return (
    <SelectPrimitive.Item
      value={value}
      className="flex h-10 cursor-pointer items-center bg-white px-3 py-2 text-sm leading-[1.7] whitespace-nowrap text-main outline-hidden hover:bg-gray-50 focus:bg-gray-50 data-[state=checked]:bg-primary_lighten data-[state=checked]:font-bold data-[state=checked]:text-primary"
    >
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
  );
}
